import { Directive, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { pairwise, startWith } from 'rxjs/operators';
import { GroupInterface, GroupMemberInterface } from 'src/app/interfaces/groups/group.interface';
import { GroupsService } from 'src/app/services/groups/groups.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';

@Directive()
export abstract class BaseGroupForm implements OnInit {
  @Input() data?: GroupInterface;
  @Input() initialValues: string[] | boolean[] = [];
  @Input() formSnapshots!: Map<GroupMemberInterface, any>;
  @Input() close = () => {};
  @Input()
  set groupInterface(value: GroupMemberInterface) {
    this._groupInterface = value;
    this.canSave = false;
    this.isEmailGroup = value === GroupMemberInterface.EmailAddress;
    if (this.form) this.handleGroupInterfaceChange();
  }
  @Output() saved: EventEmitter<{ data: any }> = new EventEmitter<{ data: any }>();
  @Output() memberChangeCompleted: EventEmitter<void> = new EventEmitter<void>();

  get groupInterface() {
    return this._groupInterface;
  }

  form!: FormGroup;
  protected subscription?: Subscription;
  protected groupId?: string;
  protected isEmailGroup = false;
  protected canSave = false;
  protected _groupInterface!: GroupMemberInterface;

  constructor(
    protected fb: FormBuilder,
    protected spinnerService: SpinnerService,
    protected snackBar: MatSnackBar,
    protected groupsService: GroupsService,
  ) {
  }
  
  async ngOnInit(): Promise<void> {
    if (this.data) {
      this.groupId = this.data.id;
    }
    this.initForm();
    await this.addFormItemsControl();
    this.trackAndUpdate();
    this.registerFormListeners();
  }

  protected initForm(): void {
    this.form = this.fb.group({
      name: [{ value: this.data?.name ?? '', disabled: !!this.data }, Validators.required],
      description: [
        { value: this.data?.description ?? '', disabled: !!this.data },
        Validators.required,
      ],
    });
  }

  trackAndUpdate() {
    if (!this.data) return;

    const formControlName = this.isEmailGroup ? 'emailAddresses' : 'items';
    const formArray = this.form.get(formControlName) as FormArray;

    this.subscription = formArray.valueChanges
      .pipe(startWith(this.isEmailGroup ? this.data.members : this.initialValues), pairwise())
      .subscribe(async ([previous, current]) => {
        const diff = this.getDiffIndices(previous, current);
        if (!diff || !this.groupId) return;

        await this.handleMemberChange(diff, previous);
      });
  }

  protected async handleMemberChange(
    diff: { value: boolean; idx?: number; email?: string },
    previousValues: any[],
  ): Promise<void> {
    this.spinnerService.show('Please wait...');

    try {
      const memberInfo = this.getMemberInfo(diff);
      await this.performMemberOperation(diff.value, memberInfo.id);

      this.showSuccessMessage(diff.value, memberInfo.name);
      this.memberChangeCompleted.emit();
    } catch (error) {
      console.error('Member operation failed:', error);
      this.showErrorMessage(diff.value, this.getMemberInfo(diff).name);
      this.revertFormState(previousValues);
    } finally {
      this.spinnerService.hide();
    }
  }

  private async performMemberOperation(isAdd: boolean, memberId: string): Promise<void> {
    if (!this.groupId) {
      throw new Error('Group ID is required');
    }

    return isAdd
      ? this.groupsService.addMemberToGroupById(this.groupId, memberId)
      : this.groupsService.removeMemberById(this.groupId, memberId);
  }

  private showSuccessMessage(isAdd: boolean, memberName: string): void {
    const action = isAdd ? 'Added' : 'Removed';
    const preposition = isAdd ? 'to' : 'from';

    this.snackBar.open(`${action} ${memberName} ${preposition} group.`, undefined, {
      duration: 2000,
    });
  }

  private showErrorMessage(isAdd: boolean, memberName: string): void {
    const action = isAdd ? 'adding' : 'removing';
    const preposition = isAdd ? 'to' : 'from';

    this.snackBar.open(`Error ${action} ${memberName} ${preposition} group.`, 'Error', {
      duration: 2000,
    });
  }

  protected abstract save(): Promise<void>;

  protected abstract registerFormListeners(): void;

  protected abstract getDiffIndices(
    previous: any[],
    current: any[],
  ): { value: boolean; idx?: number; email?: string } | null;

  protected abstract addFormItemsControl(): Promise<void>;

  protected abstract getMemberInfo(diff: { value: boolean; idx?: number; email?: string }): {
    id: string;
    name: string;
  };

  protected abstract revertFormState(previousValues: any[]): void;

  protected abstract handleGroupInterfaceChange(): void;
}
